import cn from 'classnames'
import React, { ComponentPropsWithRef, MouseEvent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import MoreIcon from '../../lib/icons/more-icon/more-icon'
import useOnClickOutside from '../hooks/useOnClickOutside'

import { IconButton } from './IconButton'
import Menu from './Menu'

const SIconButton = styled(IconButton)`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: unset;
    border-radius: 50%;
    padding: ${({ theme }) => theme.sizing.padding.s};
`

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
`

const MenuListItem = styled.li<{ disabled?: boolean }>`
    cursor: pointer;
    padding: 10px 20px;
    transition: background-color ${({ theme }) => theme.transition};
    line-height: 1;
    color: ${({ theme }) => theme.brandColors.CA_Dark_Gray};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: grid;
    grid-template-columns: 24px 1fr;
    grid-template-areas: 'icon text';
    align-items: center;

    #icon {
        grid-area: icon;
    }

    #text {
        grid-area: text;
    }

    &:hover {
        background-color: ${({ theme }) => theme.brandColors.CA_Black + '0a'};
    }

    &:not(:last-child) {
        border-bottom: 1px solid ${({ theme }) => theme.brandColors.CA_Light_Gray};
    }

    ${({ disabled }) =>
        disabled
            ? `
        cursor: not-allowed;
        opacity: 0.6;
        `
            : ''}
`

interface IMenuItemProps extends ComponentPropsWithRef<'li'> {
    text: string
    icon?: React.ReactElement
    disabled?: boolean
    disabledReason?: string
    title?: string
}

export const DropdownMenuItem = ({ text, icon, disabledReason, disabled, title, onClick, ...rest }: IMenuItemProps) => {
    return (
        <MenuListItem
            title={disabled ? disabledReason : title}
            disabled={disabled}
            onClick={(e) => {
                if (disabled) return
                if (typeof onClick === 'function') {
                    onClick(e)
                }
            }}
            {...rest}
        >
            <span id="text">{text}</span>
            {icon && <span id="icon">{icon}</span>}
        </MenuListItem>
    )
}

interface IDropdownMenuProps extends ComponentPropsWithRef<'div'> {}

const DropdownMenu = ({ children: menuItems, id, ...rest }: IDropdownMenuProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const menuContainerRef = useRef<HTMLDivElement>(null)
    const dropdownMenuRef = useRef<HTMLUListElement>(null)

    const onClickHandler = (e: Event) => {
        e.stopPropagation()
        setIsOpen(false)
    }

    useOnClickOutside(dropdownMenuRef, onClickHandler)

    useEffect(() => {
        if (!dropdownMenuRef.current || !menuContainerRef) {
            return
        }

        const containerBoundingRect = menuContainerRef?.current?.getBoundingClientRect()
        const menuBoundingRect = dropdownMenuRef?.current?.getBoundingClientRect()

        if (isOpen && containerBoundingRect && menuBoundingRect) {
            dropdownMenuRef.current.style.left = `${
                containerBoundingRect.x + containerBoundingRect.width - menuBoundingRect.width
            }px`
            dropdownMenuRef.current.style.top = `${containerBoundingRect.y + containerBoundingRect.height}px`
        }
    }, [isOpen])

    useEffect(() => {
        const handleScroll = () => setIsOpen(false)

        window.addEventListener('scroll', handleScroll)
        window.addEventListener('resize', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleScroll)
        }
    }, [])

    return (
        <MenuContainer ref={menuContainerRef} data-testid="dropdown-menu-container" {...rest}>
            <SIconButton
                onClick={(e: MouseEvent) => {
                    e.stopPropagation()
                    setIsOpen(!isOpen)
                }}
                data-testid={`${id ? id : 'menu'}-toggle-btn`}
            >
                <MoreIcon size={20} />
            </SIconButton>
            {isOpen &&
                createPortal(
                    <Menu
                        ref={dropdownMenuRef}
                        onClick={onClickHandler}
                        className={cn({ open: isOpen })}
                        data-testid="dropdown-menu"
                    >
                        {menuItems}
                    </Menu>,
                    document.body
                )}
        </MenuContainer>
    )
}

export default DropdownMenu
